/**
 * Option Chain Frame Component
 * Shows option chain table with quick order entry
 * Supports drag-drop and lock functionality
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import aliceBlueWS from '../../services/aliceBlueWebSocket';
import api from '../../services/api';

const OptionChainFrame = ({ position, symbol, isLocked, onRemove, onToggleLock, livePrices, wsConnected, isMasterUser, userGroups, onGroupOrder }) => {
  const { userSession, userId } = useSelector((state) => state.auth);
  const [optionData, setOptionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedCeStrike, setSelectedCeStrike] = useState(null);
  const [selectedPeStrike, setSelectedPeStrike] = useState(null);
  const [qtyPopup, setQtyPopup] = useState({ show: false, optionType: '', strike: null, ltp: 0, defaultQty: '25', token: null, tradingSymbol: '' });
  const [lotSize, setLotSize] = useState(25); // Store lot size from API
  const [baselineCeSum, setBaselineCeSum] = useState(0);
  const [baselinePeSum, setBaselinePeSum] = useState(0);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [orderPopup, setOrderPopup] = useState({ 
    show: false, 
    strike: null, 
    optionType: '', 
    side: '', 
    ltp: 0, 
    token: null, 
    tradingSymbol: '',
    qty: 0,
    price: 0
  });
  const [depthData, setDepthData] = useState({});
  const [marketType, setMarketType] = useState('LIMIT'); // LIMIT or MARKET
  const [segment, setSegment] = useState('NRML'); // NRML or MIS
  const [orderMessage, setOrderMessage] = useState({ show: false, text: '', type: '' });
  const refreshIntervalRef = useRef(null);

  // Draggable popup state
  const [popupPos, setPopupPos] = useState({ x: 0, y: 0 });
  const [isDraggingPopup, setIsDraggingPopup] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const popupRef = useRef(null);

  const handlePopupMouseDown = useCallback((e) => {
    if (!popupRef.current) return;
    setIsDraggingPopup(true);
    const rect = popupRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    e.preventDefault();
  }, []);

  // Reset popup position when popup opens fresh
  useEffect(() => {
    if (orderPopup.show) {
      setPopupPos({ x: 0, y: 0 });
    }
  }, [orderPopup.show]);

  useEffect(() => {
    if (!isDraggingPopup) return;
    const handleMouseMove = (e) => {
      const newX = e.clientX - dragOffset.current.x;
      const newY = e.clientY - dragOffset.current.y;
      setPopupPos({ x: newX, y: newY });
    };
    const handleMouseUp = () => setIsDraggingPopup(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingPopup]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: position,
    disabled: isLocked
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Reset all state when symbol changes
  useEffect(() => {
    if (!symbol) return;
    
    // Clear selected strikes
    setSelectedCeStrike(null);
    setSelectedPeStrike(null);
    
    // Reset baseline values
    setBaselineCeSum(0);
    setBaselinePeSum(0);
    
    // Clear option data to force fresh fetch
    setOptionData(null);
    
    // Clear depth data
    setDepthData({});
    
    console.log(`🔄 Symbol changed to ${symbol}, resetting all state`);
  }, [symbol]);

  // Load selected strikes from localStorage on mount or when symbol changes
  useEffect(() => {
    if (!symbol) return;
    
    try {
      const savedCeStrike = localStorage.getItem(`nseOptions_selected_ce_${symbol}`);
      const savedPeStrike = localStorage.getItem(`nseOptions_selected_pe_${symbol}`);
      
      if (savedCeStrike) {
        const parsed = JSON.parse(savedCeStrike);
        setSelectedCeStrike(parsed);
        console.log(`✅ Loaded CE strike from localStorage for ${symbol}:`, parsed);
      }
      
      if (savedPeStrike) {
        const parsed = JSON.parse(savedPeStrike);
        setSelectedPeStrike(parsed);
        console.log(`✅ Loaded PE strike from localStorage for ${symbol}:`, parsed);
      }
    } catch (error) {
      console.error('Failed to load selected strikes from localStorage:', error);
    }
  }, [symbol]);

  // Load baseline from database on mount
  useEffect(() => {
    const loadBaseline = async () => {
      // Check for both 'expiry' and 'expiryDate' fields
      const expiryDate = optionData?.expiry || optionData?.expiryDate;
      if (!symbol || !expiryDate) return;
      
      try {
        // Format expiry date for API call
        let formattedExpiry = expiryDate;
        const monthMap = { 
          JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06', 
          JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12',
          Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
          Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12'
        };
        
        // Handle format like "24FEB26" (DDMMMYY)
        if (/^\d{2}[A-Z]{3}\d{2}$/i.test(expiryDate)) {
          const day = expiryDate.substring(0, 2);
          const monthStr = expiryDate.substring(2, 5).toUpperCase();
          const yearShort = expiryDate.substring(5, 7);
          const year = '20' + yearShort;
          const month = monthMap[monthStr] || '01';
          formattedExpiry = `${year}-${month}-${day}`;
        }
        // Handle format like "31-Jan-2026" (DD-MMM-YYYY)
        else if (expiryDate.includes('-')) {
          const parts = expiryDate.split('-');
          if (parts.length === 3) {
            const day = parts[0].padStart(2, '0');
            const month = monthMap[parts[1]] || '01';
            const year = parts[2];
            formattedExpiry = `${year}-${month}-${day}`;
          }
        }
        
        const response = await api.get(
          `/nse-options-trade/baseline/${symbol}/${formattedExpiry}`,
          { headers: { Authorization: `Bearer ${userSession}` } }
        );
        
        if (response.data.success && response.data.hasBaseline) {
          const { ceLtpSum, peLtpSum } = response.data.data;
          setBaselineCeSum(ceLtpSum);
          setBaselinePeSum(peLtpSum);
          console.log(`✅ Loaded baseline from DB for ${symbol} - CE: ${ceLtpSum}, PE: ${peLtpSum}`);
        }
      } catch (error) {
        console.error('Failed to load baseline from database:', error);
        // Fallback to localStorage if database fails
        const savedCeBaseline = localStorage.getItem(`nseOptions_baseline_ce_${symbol}`);
        const savedPeBaseline = localStorage.getItem(`nseOptions_baseline_pe_${symbol}`);
        
        if (savedCeBaseline) setBaselineCeSum(parseFloat(savedCeBaseline));
        if (savedPeBaseline) setBaselinePeSum(parseFloat(savedPeBaseline));
      }
    };
    
    loadBaseline();
  }, [symbol, optionData?.expiry, optionData?.expiryDate, userSession]);

  // Component will naturally re-render when livePrices prop changes
  // No need for explicit useEffect logging that triggers on every update

  // Fetch option chain data
  const fetchOptionChain = async () => {
    if (!symbol) return;

    // Debug WIPRO
    if (symbol.toUpperCase().includes('WIPRO')) {
      console.log(`🔍 [WIPRO DEBUG] Fetching option chain for symbol: "${symbol}"`);
    }

    try {
      setLoading(true);
      const response = await api.get(`/nse-options-trade/option-chain?symbol=${symbol}`, {
        headers: { Authorization: `Bearer ${userSession}` }
      });

      // Debug WIPRO response
      if (symbol.toUpperCase().includes('WIPRO')) {
        console.log(`🔍 [WIPRO DEBUG] Response:`, response.data);
        if (response.data.rows && response.data.rows.length > 0) {
          console.log(`🔍 [WIPRO DEBUG] First 3 rows:`, response.data.rows.slice(0, 3));
        }
      }

      if (response.data.success) {
        setOptionData(response.data);
        // Store lot size from API response
        if (response.data.lotSize) {
          setLotSize(response.data.lotSize);
        }
      }
    } catch (error) {
      console.error('Failed to fetch option chain:', error);
      if (symbol.toUpperCase().includes('WIPRO')) {
        console.error('🔍 [WIPRO DEBUG] Error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Analyse button - Calculate and store baseline
  const handleAnalyse = async () => {
    if (!optionData || !optionData.rows) {
      alert('No option chain data available');
      return;
    }

    // Get expiry date from API response (field is 'expiry', not 'expiryDate')
    const expiryDate = optionData.expiry || optionData.expiryDate;
    
    if (!expiryDate) {
      alert('Expiry date not available');
      return;
    }

    try {
      // Calculate sum of all CE LTPs using live prices (same as strength calculation)
      const ceSum = optionData.rows.reduce((sum, row) => {
        const liveLtp = getLivePrice('NFO', row.ceToken) || row.ceLtp;
        return sum + (liveLtp || 0);
      }, 0);
      
      // Calculate sum of all PE LTPs using live prices (same as strength calculation)
      const peSum = optionData.rows.reduce((sum, row) => {
        const liveLtp = getLivePrice('NFO', row.peToken) || row.peLtp;
        return sum + (liveLtp || 0);
      }, 0);

      console.log(`📊 Calculating baseline for ${symbol} - CE Sum: ${ceSum.toFixed(2)}, PE Sum: ${peSum.toFixed(2)}`);

      // Format expiry date for database
      let formattedExpiry = expiryDate;
      const monthMap = { 
        JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06', 
        JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12',
        Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
        Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12'
      };
      
      // Handle format like "24FEB26" (DDMMMYY)
      if (/^\d{2}[A-Z]{3}\d{2}$/i.test(expiryDate)) {
        const day = expiryDate.substring(0, 2);
        const monthStr = expiryDate.substring(2, 5).toUpperCase();
        const yearShort = expiryDate.substring(5, 7);
        const year = '20' + yearShort; // Assuming 20xx century
        const month = monthMap[monthStr] || '01';
        formattedExpiry = `${year}-${month}-${day}`;
      }
      // Handle format like "31-Jan-2026" (DD-MMM-YYYY)
      else if (expiryDate.includes('-')) {
        const parts = expiryDate.split('-');
        if (parts.length === 3) {
          const day = parts[0].padStart(2, '0');
          const month = monthMap[parts[1]] || '01';
          const year = parts[2];
          formattedExpiry = `${year}-${month}-${day}`;
        }
      }

      // Save to database
      const response = await api.post(
        '/nse-options-trade/baseline',
        {
          symbolName: symbol,
          expiryDate: formattedExpiry,
          ceLtpSum: ceSum,
          peLtpSum: peSum
        },
        { headers: { Authorization: `Bearer ${userSession}` } }
      );

      if (response.data.success) {
        // Update state
        setBaselineCeSum(ceSum);
        setBaselinePeSum(peSum);

        // Also save to localStorage as backup
        localStorage.setItem(`nseOptions_baseline_ce_${symbol}`, ceSum.toString());
        localStorage.setItem(`nseOptions_baseline_pe_${symbol}`, peSum.toString());

        console.log(`✅ Baseline saved to DB for ${symbol} - CE: ${ceSum.toFixed(2)}, PE: ${peSum.toFixed(2)}`);
        
        // Show success message
        setOrderMessage({ show: true, text: 'Baseline saved successfully', type: 'success' });
        setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 3000);
      }
    } catch (error) {
      console.error('❌ Failed to save baseline to database:', error);
      alert('Failed to save baseline. Please try again.');
    }
  };

  // Initial fetch only (no polling - use WebSocket for live updates)
  useEffect(() => {
    if (symbol) {
      fetchOptionChain();
    }
    // Clean up on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [symbol, userSession]);

  // Subscribe to WebSocket for real-time prices
  useEffect(() => {
    if (optionData && optionData.rows && wsConnected) {
      const instruments = [];
      
      optionData.rows.forEach(row => {
        // Subscribe to CE strikes
        if (row.ceToken) {
          instruments.push({
            exchange: 'NFO',
            token: row.ceToken
          });
        }
        // Subscribe to PE strikes
        if (row.peToken) {
          instruments.push({
            exchange: 'NFO',
            token: row.peToken
          });
        }
      });

      if (instruments.length > 0) {
        console.log(`📡 [${symbol}] Subscribing to ${instruments.length} instruments`);
        aliceBlueWS.subscribe(instruments);
        // Also subscribe to depth data
        console.log(`📊 [${symbol}] Subscribing to depth data for ${instruments.length} instruments`);
        aliceBlueWS.subscribeDepth(instruments);
      }

      return () => {
        // Optionally unsubscribe when component unmounts
        // aliceBlueWS.unsubscribe(instruments);
      };
    }
  }, [optionData, wsConnected, symbol]);

  // Listen to depth data updates from WebSocket
  // Note: Parent component (NseOptionsTrade) handles price updates via its own wildcard subscriber
  // We only need to handle depth-specific data here
  useEffect(() => {
    if (!wsConnected || !optionData?.rows) return;

    const handleDepthUpdate = (data) => {
      // Only process depth data, not price updates (those are handled by parent)
      if (data.type === 'depth') {
        const key = `${data.exchange}|${data.token}`;
        
        // Always update depth data, even for partial updates
        // Merge with existing data to preserve fields not included in the update
        setDepthData(prev => {
          const existing = prev[key] || {};
          const merged = { ...existing };
          
          // Update all fields that are present in the new data
          // This preserves fields from previous updates while adding/updating new ones
          Object.keys(data).forEach(field => {
            if (data[field] !== undefined) {
              merged[field] = data[field];
            }
          });
          
          // Log depth updates for debugging
          if (merged.bp1 || merged.sp1) {
            const buyLevels = [1,2,3,4,5].filter(i => merged[`bp${i}`]).length;
            const sellLevels = [1,2,3,4,5].filter(i => merged[`sp${i}`]).length;
            console.log(`📊 [${symbol}] Depth update for ${key}: ${buyLevels} buy levels, ${sellLevels} sell levels`);
          }
          
          return {
            ...prev,
            [key]: merged
          };
        });
      }
    };

    // Subscribe to depth data updates
    aliceBlueWS.addSubscriber('*', handleDepthUpdate);

    return () => {
      aliceBlueWS.removeSubscriber('*', handleDepthUpdate);
      // Don't clear depth data on cleanup - preserve for next render
    };
  }, [wsConnected, optionData, symbol]);

  // Helper function to get live price from WebSocket data
  const getLivePrice = (exchange, token) => {
    if (!token) return null;
    // WebSocket uses pipe format: exchange|token (NOT underscore)
    const key = `${exchange}|${token}`;
    const priceData = livePrices[key];
    // Check for both ltp and lp fields (depth data uses 'lp')
    return priceData?.ltp || priceData?.lp || null;
  };

  // Handle double click on strike - opens quantity popup
  const handleStrikeDoubleClick = (strike, optionType, ltp, token, tradingSymbol) => {
    setQtyPopup({
      show: true,
      optionType: optionType,
      strike: strike,
      ltp: ltp,
      defaultQty: lotSize.toString(),
      token: token,
      tradingSymbol: tradingSymbol
    });
  };

  // Handle order placement - same pattern as TradingView Quick Order Entry
  const handleOrder = async (optionType, side) => {
    const selectedStrike = optionType === 'CE' ? selectedCeStrike : selectedPeStrike;
    
    if (!selectedStrike) {
      setOrderMessage({ show: true, text: 'Please select a strike by double-clicking on the table', type: 'error' });
      setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 3000);
      return;
    }

    if (!selectedStrike.token || !selectedStrike.tradingSymbol) {
      setOrderMessage({ show: true, text: 'Strike data incomplete. Please re-select the strike.', type: 'error' });
      setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 3000);
      return;
    }

    const qty = selectedStrike.qty || lotSize;
    const livePrice = getLivePrice('NFO', selectedStrike.token) || selectedStrike.ltp || 0;

    const orderPayload = {
      exchange: 'NFO',
      tradingSymbol: selectedStrike.tradingSymbol,
      instrumentId: selectedStrike.token,
      transactionType: side,
      quantity: qty,
      price: marketType === 'MARKET' ? 0 : livePrice,
      orderType: marketType,
      validity: 'DAY',
      product: segment,
      orderComplexity: 'REGULAR',
      deviceId: '123',
      userId: userId
    };

    // If master user with groups, delegate to parent for group selection
    if (isMasterUser && userGroups && userGroups.length > 0 && onGroupOrder) {
      onGroupOrder(orderPayload);
      return;
    }

    // Place order directly
    try {
      const response = await api.post('/orders/place', orderPayload);

      if (response.data.success) {
        setOrderMessage({ show: true, text: `${side} order placed! ${symbol} ${selectedStrike.strike}${optionType}`, type: 'success' });
        setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 3000);
      } else {
        setOrderMessage({ show: true, text: 'Order failed: ' + (response.data.error || 'Unknown error'), type: 'error' });
        setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 3000);
      }
    } catch (error) {
      console.error('Order placement error:', error);
      setOrderMessage({ show: true, text: 'Failed: ' + (error.response?.data?.error || error.message), type: 'error' });
      setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 3000);
    }
  };

  // Empty frame placeholder
  if (!symbol) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="bg-white/5 backdrop-blur-sm rounded-lg border-2 border-dashed border-white/30 p-3 flex items-center justify-center h-auto"
      >
        <div className="text-center text-white/50">
          <div className="text-3xl mb-2">📊</div>
          <div className="text-sm font-semibold">Position {position}</div>
          <div className="text-xs">Select Symbol</div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/30 overflow-hidden flex flex-col shadow-lg hover:shadow-xl transition-shadow"
    >
      {/* Order Message Toast */}
      {orderMessage.show && (
        <div className={`absolute top-2 right-2 z-50 px-3 py-2 rounded shadow-lg text-white text-xs font-bold ${
          orderMessage.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {orderMessage.text}
        </div>
      )}
      
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-2 py-1 flex items-center justify-between border-b border-white/30">
        <button
          onClick={handleAnalyse}
          className="text-white bg-white/20 hover:bg-white/30 px-2 py-1 rounded text-xs font-semibold transition-all"
          disabled={loading}
        >
           {/* Analyse */}
          {loading ? '⟳' : '📊'}
        </button>

        <div className="flex items-center gap-2">
          <span
            {...listeners}
            className={`cursor-${isLocked ? 'not-allowed' : 'move'} text-white text-sm`}
            title={isLocked ? 'Locked' : 'Drag to reorder'}
          >
            ⋮⋮
          </span>
          <span className="text-white font-bold text-sm">{symbol}</span>
        </div>

        <div className="flex items-center gap-1">
        
          <button
            onClick={onToggleLock}
            className={`text-white text-sm px-2 py-1 rounded transition-all ${
              isLocked ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-white/20 hover:bg-white/30'
            }`}
            title={isLocked ? 'Unlock' : 'Lock'}
          >
            {isLocked ? '🔒' : '🔓'}
          </button>
          <button
            onClick={onRemove}
            className="text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded text-xs transition-all"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Option Chain Table */}
      <div className="flex-1 overflow-auto">
        {loading && !optionData ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-white text-sm">Loading...</div>
          </div>
        ) : optionData ? (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-800/90 text-white">
              <tr>
                <th className="text-left px-2 py-1 font-semibold">CE LTP</th>
                <th className="text-center px-2 py-1 font-semibold">Strike</th>
                <th className="text-right px-2 py-1 font-semibold">PE LTP</th>
              </tr>
            </thead>
            <tbody>
              {optionData.rows.map((row, index) => {
                // Get live prices from WebSocket or fallback to cached
                const liveCeLtp = getLivePrice('NFO', row.ceToken) || row.ceLtp;
                const livePeLtp = getLivePrice('NFO', row.peToken) || row.peLtp;
                const rowId = `${index}`;
                const isHovered = hoveredRow === rowId;
                
                return (
                  <tr
                    key={index}
                    className={`border-b border-white/10 hover:bg-white/10 transition-colors relative group ${
                      row.isAtm ? 'bg-yellow-500/20 font-bold' : ''
                    }`}
                    onMouseEnter={() => setHoveredRow(rowId)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <td
                      className="text-left px-2 py-1 text-white text-sm relative"
                      onDoubleClick={() => handleStrikeDoubleClick(row.strike, 'CE', liveCeLtp, row.ceToken, row.ceTradingSymbol)}
                      title="Double-click to select"
                    >
                      {/* Buy Button - CE */}
                      {isHovered && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOrderPopup({
                              show: true,
                              strike: row.strike,
                              optionType: 'CE',
                              side: 'BUY',
                              ltp: liveCeLtp,
                              token: row.ceToken,
                              tradingSymbol: row.ceTradingSymbol,
                              qty: lotSize,
                              price: liveCeLtp
                            });
                          }}
                          className="absolute left-0.5 top-1/2 transform -translate-y-1/2 bg-green-600 hover:bg-green-700 text-white text-[11px] font-bold rounded shadow-lg transition-all z-10"
                          style={{ minWidth: '24px', height: '24px', width: '24px' }}
                          title="Buy CE"
                        >
                          B
                        </button>
                      )}
                      <span className={isHovered ? 'ml-6' : ''}>{liveCeLtp.toFixed(2)}</span>
                      {/* Sell Button - CE */}
                      {isHovered && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOrderPopup({
                              show: true,
                              strike: row.strike,
                              optionType: 'CE',
                              side: 'SELL',
                              ltp: liveCeLtp,
                              token: row.ceToken,
                              tradingSymbol: row.ceTradingSymbol,
                              qty: lotSize,
                              price: liveCeLtp
                            });
                          }}
                          className="absolute right-0.5 top-1/2 transform -translate-y-1/2 bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold rounded shadow-lg transition-all z-10"
                          style={{ minWidth: '24px', height: '24px', width: '24px' }}
                          title="Sell CE"
                        >
                          S
                        </button>
                      )}
                    </td>
                    <td className="text-center px-2 py-1 text-blue-400 font-bold text-sm">
                      {row.strike}
                      {row.isAtm && <span className="ml-1 text-yellow-400 text-xs"></span>}
                    </td>
                    <td
                      className="text-right px-2 py-1 text-white text-sm relative"
                      onDoubleClick={() => handleStrikeDoubleClick(row.strike, 'PE', livePeLtp, row.peToken, row.peTradingSymbol)}
                      title="Double-click to select"
                    >
                      {/* Buy Button - PE */}
                      {isHovered && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOrderPopup({
                              show: true,
                              strike: row.strike,
                              optionType: 'PE',
                              side: 'BUY',
                              ltp: livePeLtp,
                              token: row.peToken,
                              tradingSymbol: row.peTradingSymbol,
                              qty: lotSize,
                              price: livePeLtp
                            });
                          }}
                          className="absolute left-0.5 top-1/2 transform -translate-y-1/2 bg-green-600 hover:bg-green-700 text-white text-[11px] font-bold rounded shadow-lg transition-all z-10"
                          style={{ minWidth: '24px', height: '24px', width: '24px' }}
                          title="Buy PE"
                        >
                          B
                        </button>
                      )}
                      <span className={isHovered ? 'mr-6' : ''}>{livePeLtp.toFixed(2)}</span>
                      {/* Sell Button - PE */}
                      {isHovered && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOrderPopup({
                              show: true,
                              strike: row.strike,
                              optionType: 'PE',
                              side: 'SELL',
                              ltp: livePeLtp,
                              token: row.peToken,
                              tradingSymbol: row.peTradingSymbol,
                              qty: lotSize,
                              price: livePeLtp
                            });
                          }}
                          className="absolute right-0.5 top-1/2 transform -translate-y-1/2 bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold rounded shadow-lg transition-all z-10"
                          style={{ minWidth: '24px', height: '24px', width: '24px' }}
                          title="Sell PE"
                        >
                          S
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-white/50 text-sm">No data available</div>
          </div>
        )}
      </div>

      {/* Quick Order Entry - styled like TradingView Quick Order Entry */}
      <div className="border-t border-white/30 bg-gray-900/80">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="text-white/60 font-semibold bg-gradient-to-r from-indigo-700/60 to-blue-700/60">
              <th className="px-1 py-0.5 text-center">Entry</th>
              <th className="px-1 py-0.5 text-center">LTP</th>
              <th className="px-1 py-0.5 text-center">Diff</th>
              <th className="px-1 py-0.5 text-center">Str</th>
              <th className="px-1 py-0.5 text-center" colSpan="2">Action</th>
              <th className="px-1 py-0.5 text-center">Strike</th>
            </tr>
          </thead>
          <tbody>
            {/* CE Row */}
            <tr className="border-b border-white/10 h-[26px]">
              {(() => {
                const ceLtp = selectedCeStrike ? getLivePrice('NFO', (() => {
                  if (!optionData?.rows) return null;
                  const row = optionData.rows.find(r => r.strike === selectedCeStrike.strike);
                  return row?.ceToken;
                })()) : null;
                const ceEntry = selectedCeStrike?.ltp || null;
                const ceDiff = (ceLtp && ceEntry) ? (ceLtp - ceEntry) : null;
                // Strength: current sum - baseline
                let ceStrength = null;
                if (optionData?.rows && baselineCeSum !== 0) {
                  const currentSum = optionData.rows.reduce((sum, row) => {
                    const live = getLivePrice('NFO', row.ceToken) || row.ceLtp;
                    return sum + (live || 0);
                  }, 0);
                  ceStrength = currentSum - baselineCeSum;
                }
                return (
                  <>
                    <td className="px-1 py-0.5 text-center text-blue-300 text-sm" title={selectedCeStrike ? `${selectedCeStrike.strike}CE` : ''}>
                      {ceEntry ? ceEntry.toFixed(1) : '-'}
                    </td>
                    <td className="px-1 py-0.5 text-center text-white text-sm">
                      {ceLtp ? ceLtp.toFixed(1) : '-'}
                    </td>
                    <td className={`px-1 py-0.5 text-center text-sm ${ceDiff !== null ? (ceDiff >= 0 ? 'text-green-400' : 'text-red-400') : 'text-white/50'}`}>
                      {ceDiff !== null ? ceDiff.toFixed(1) : '-'}
                    </td>
                    <td className={`px-1.5 py-0.5 text-center text-sm ${ceStrength !== null ? (ceStrength >= 0 ? 'text-green-400' : 'text-red-400') : 'text-white/50'}`}>
                      {ceStrength !== null ? ceStrength.toFixed(0) : '-'}
                    </td>
                    <td className="px-0.5 py-0.5 text-center text-sm">
                      <button
                        onClick={() => {
                          if (!selectedCeStrike) return;
                          setOrderPopup({
                            show: true,
                            strike: selectedCeStrike.strike,
                            optionType: 'CE',
                            side: 'BUY',
                            ltp: ceLtp || selectedCeStrike.ltp || 0,
                            token: selectedCeStrike.token,
                            tradingSymbol: selectedCeStrike.tradingSymbol,
                            qty: selectedCeStrike.qty || lotSize
                          });
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-0.5 rounded text-[10px] font-bold transition-all disabled:opacity-40"
                        disabled={!selectedCeStrike}
                      >B</button>
                    </td>
                    <td className="px-0.5 py-0.5 text-center text-sm">
                      <button
                        onClick={() => {
                          if (!selectedCeStrike) return;
                          setOrderPopup({
                            show: true,
                            strike: selectedCeStrike.strike,
                            optionType: 'CE',
                            side: 'SELL',
                            ltp: ceLtp || selectedCeStrike.ltp || 0,
                            token: selectedCeStrike.token,
                            tradingSymbol: selectedCeStrike.tradingSymbol,
                            qty: selectedCeStrike.qty || lotSize
                          });
                        }}
                        className="bg-red-600 hover:bg-red-700 text-white px-2 py-0.5 rounded text-[10px] font-bold transition-all disabled:opacity-40"
                        disabled={!selectedCeStrike}
                      >S</button>
                    </td>
                    <td className="px-1 py-0.5 text-center text-yellow-100 font-bold text-sm">
                      {selectedCeStrike ? `${selectedCeStrike.strike}CE` : '-'}
                    </td>
                  </>
                );
              })()}
            </tr>
            {/* PE Row */}
            <tr className="h-[26px]">
              {(() => {
                const peLtp = selectedPeStrike ? getLivePrice('NFO', (() => {
                  if (!optionData?.rows) return null;
                  const row = optionData.rows.find(r => r.strike === selectedPeStrike.strike);
                  return row?.peToken;
                })()) : null;
                const peEntry = selectedPeStrike?.ltp || null;
                const peDiff = (peLtp && peEntry) ? (peLtp - peEntry) : null;
                // Strength: current sum - baseline
                let peStrength = null;
                if (optionData?.rows && baselinePeSum !== 0) {
                  const currentSum = optionData.rows.reduce((sum, row) => {
                    const live = getLivePrice('NFO', row.peToken) || row.peLtp;
                    return sum + (live || 0);
                  }, 0);
                  peStrength = currentSum - baselinePeSum;
                }
                return (
                  <>
                    <td className="px-1 py-0.5 text-center text-purple-300 text-sm" title={selectedPeStrike ? `${selectedPeStrike.strike}PE` : ''}>
                      {peEntry ? peEntry.toFixed(1) : '-'}
                    </td>
                    <td className="px-1 py-0.5 text-center text-white text-sm">
                      {peLtp ? peLtp.toFixed(1) : '-'}
                    </td>
                    <td className={`px-1 py-0.5 text-center text-sm ${peDiff !== null ? (peDiff >= 0 ? 'text-green-400' : 'text-red-400') : 'text-white/50'}`}>
                      {peDiff !== null ? peDiff.toFixed(1) : '-'}
                    </td>
                    <td className={`px-1.5 py-0.5 text-center text-sm ${peStrength !== null ? (peStrength >= 0 ? 'text-green-400' : 'text-red-400') : 'text-white/50'}`}>
                      {peStrength !== null ? peStrength.toFixed(0) : '-'}
                    </td>
                    <td className="px-0.5 py-0.5 text-center text-sm">
                      <button
                        onClick={() => {
                          if (!selectedPeStrike) return;
                          setOrderPopup({
                            show: true,
                            strike: selectedPeStrike.strike,
                            optionType: 'PE',
                            side: 'BUY',
                            ltp: peLtp || selectedPeStrike.ltp || 0,
                            token: selectedPeStrike.token,
                            tradingSymbol: selectedPeStrike.tradingSymbol,
                            qty: selectedPeStrike.qty || lotSize
                          });
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-0.5 rounded text-[10px] font-bold transition-all disabled:opacity-40"
                        disabled={!selectedPeStrike}
                      >B</button>
                    </td>
                    <td className="px-0.5 py-0.5 text-center text-sm">
                      <button
                        onClick={() => {
                          if (!selectedPeStrike) return;
                          setOrderPopup({
                            show: true,
                            strike: selectedPeStrike.strike,
                            optionType: 'PE',
                            side: 'SELL',
                            ltp: peLtp || selectedPeStrike.ltp || 0,
                            token: selectedPeStrike.token,
                            tradingSymbol: selectedPeStrike.tradingSymbol,
                            qty: selectedPeStrike.qty || lotSize
                          });
                        }}
                        className="bg-red-600 hover:bg-red-700 text-white px-2 py-0.5 rounded text-[10px] font-bold transition-all disabled:opacity-40"
                        disabled={!selectedPeStrike}
                      >S</button>
                    </td>
                    <td className="px-1 py-0.5 text-center text-yellow-100 font-bold text-sm">
                      {selectedPeStrike ? `${selectedPeStrike.strike}PE` : '-'}
                    </td>
                  </>
                );
              })()}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Quantity Popup Modal - Compact */}
      {qtyPopup.show && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl p-2.5 w-56">
            <div className="text-center mb-2">
              <h3 className="text-xs font-bold text-gray-800">
                {symbol} {qtyPopup.strike}{qtyPopup.optionType}
              </h3>
              <span className="text-[10px] text-blue-600 font-semibold">LTP: ₹{qtyPopup.ltp.toFixed(2)}</span>
            </div>

            <div className="mb-2">
              <div className="flex items-center justify-center gap-1.5">
                <button
                  onClick={() => {
                    const currentQty = parseInt(qtyPopup.defaultQty) || 0;
                    const newQty = Math.max(0, currentQty - lotSize);
                    setQtyPopup({ ...qtyPopup, defaultQty: newQty.toString() });
                  }}
                  className="w-7 h-7 flex items-center justify-center rounded bg-red-500 hover:bg-red-600 text-white font-bold text-base transition-colors"
                >
                  −
                </button>
                <input
                  type="number"
                  value={qtyPopup.defaultQty}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || parseInt(value) >= 0) {
                      setQtyPopup({ ...qtyPopup, defaultQty: value });
                    }
                  }}
                  className="w-16 text-center text-base font-bold text-gray-800 border border-gray-300 rounded h-7 focus:border-blue-500 focus:outline-none"
                  min="0"
                />
                <button
                  onClick={() => {
                    const currentQty = parseInt(qtyPopup.defaultQty) || 0;
                    const newQty = currentQty + lotSize;
                    setQtyPopup({ ...qtyPopup, defaultQty: newQty.toString() });
                  }}
                  className="w-7 h-7 flex items-center justify-center rounded bg-green-500 hover:bg-green-600 text-white font-bold text-base transition-colors"
                >
                  +
                </button>
              </div>
              <p className="text-[9px] text-gray-500 mt-1 text-center">
                Lot Size: {lotSize}
              </p>
            </div>

            <div className="flex gap-1.5">
              <button
                onClick={() => {
                  const strikeData = {
                    strike: qtyPopup.strike,
                    ltp: qtyPopup.ltp,
                    qty: parseInt(qtyPopup.defaultQty) || lotSize,
                    token: qtyPopup.token,
                    tradingSymbol: qtyPopup.tradingSymbol
                  };
                  
                  if (qtyPopup.optionType === 'CE') {
                    setSelectedCeStrike(strikeData);
                    // Save to localStorage
                    localStorage.setItem(`nseOptions_selected_ce_${symbol}`, JSON.stringify(strikeData));
                  } else {
                    setSelectedPeStrike(strikeData);
                    // Save to localStorage
                    localStorage.setItem(`nseOptions_selected_pe_${symbol}`, JSON.stringify(strikeData));
                  }
                  
                  setQtyPopup({ show: false, optionType: '', strike: null, ltp: 0, defaultQty: lotSize.toString(), token: null, tradingSymbol: '' });
                }}
                className="flex-1 px-2 py-1.5 rounded text-[11px] font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                Confirm
              </button>
              <button
                onClick={() => setQtyPopup({ show: false, optionType: '', strike: null, ltp: 0, defaultQty: lotSize.toString(), token: null, tradingSymbol: '' })}
                className="flex-1 px-2 py-1.5 rounded text-[11px] font-bold text-gray-700 bg-gray-300 hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Order Popup - Centered on Page */}
      {orderPopup.show && (() => {
        // Subscribe to depth data when popup opens
        if (wsConnected && orderPopup.token) {
          const instruments = [{ exchange: 'NFO', token: orderPopup.token }];
          console.log(`📊 [Order Popup] Re-subscribing to depth for ${orderPopup.tradingSymbol} (${orderPopup.token})`);
          aliceBlueWS.subscribeDepth(instruments);
        }
        return ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-30 z-[99999]" onClick={() => setOrderPopup({ show: false, strike: null, optionType: '', side: '', ltp: 0, token: null, tradingSymbol: '', qty: 0 })}>
            <div
              ref={popupRef}
              className="bg-gray-800 rounded-lg shadow-2xl p-4 w-96 border-2 border-gray-600 absolute"
              style={{
                left: popupPos.x || '50%',
                top: popupPos.y || '50%',
                transform: (popupPos.x === 0 && popupPos.y === 0) ? 'translate(-50%, -50%)' : 'none',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header - Draggable */}
              <div
                className="flex justify-between items-center mb-3 pb-2 border-b border-gray-700 cursor-move select-none"
                onMouseDown={handlePopupMouseDown}
              >
                <div>
                  <h3 className="text-white font-bold text-lg">
                    {orderPopup.side} {orderPopup.optionType} @ {orderPopup.strike}
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">{orderPopup.tradingSymbol}</p>
                </div>
                <button
                  onClick={() => setOrderPopup({ show: false, strike: null, optionType: '', side: '', ltp: 0, token: null, tradingSymbol: '', qty: 0 })}
                  className="text-gray-400 hover:text-white text-2xl leading-none font-bold"
                >
                  ×
                </button>
              </div>

              {/* LTP */}
              <div className="mb-3 bg-gray-900 px-3 py-2 rounded">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400 font-semibold">LTP:</span>
                  <span className="text-white font-bold text-base">₹{orderPopup.ltp.toFixed(2)}</span>
                </div>
              </div>

              {/* Market Depth */}
              {(() => {
              const depthKey = `NFO|${orderPopup.token}`;
              const depth = depthData[depthKey];
              
              // Log depth data for debugging
              console.log(`📊 [Order Popup] Depth data for ${depthKey}:`, depth);
              
              if (depth && (depth.bp1 || depth.sp1)) {
                // Count available levels
                const buyLevels = [1,2,3,4,5].filter(i => depth[`bp${i}`]).length;
                const sellLevels = [1,2,3,4,5].filter(i => depth[`sp${i}`]).length;
                
                return (
                  <div className="mb-3 bg-gray-900 rounded p-3">
                    <div className="text-xs text-gray-400 mb-2 text-center">
                      Market Depth ({buyLevels} buy, {sellLevels} sell levels)
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Buy Side */}
                      <div>
                        <div className="text-xs text-green-400 font-bold mb-2">BUY SIDE</div>
                        <div className="space-y-1">
                          {[1, 2, 3, 4, 5].map(i => {
                            const price = depth[`bp${i}`];
                            const qty = depth[`bq${i}`];
                            // Always show 5 levels, even if some are empty
                            return (
                              <div key={i} className="flex justify-between text-xs">
                                <span className="text-green-400 font-semibold">
                                  {price ? `${price.toFixed(2)}` : '-'}
                                </span>
                                <span className="text-gray-400">{qty || '-'}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      {/* Sell Side */}
                      <div>
                        <div className="text-xs text-red-400 font-bold mb-2">SELL SIDE</div>
                        <div className="space-y-1">
                          {[1, 2, 3, 4, 5].map(i => {
                            const price = depth[`sp${i}`];
                            const qty = depth[`sq${i}`];
                            // Always show 5 levels, even if some are empty
                            return (
                              <div key={i} className="flex justify-between text-xs">
                                <span className="text-red-400 font-semibold">
                                  {price ? `${price.toFixed(2)}` : '-'}
                                </span>
                                <span className="text-gray-400">{qty || '-'}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              } else {
                // Show loading state or message when depth data is not available
                return (
                  <div className="mb-3 bg-gray-900 rounded p-3 text-center">
                    <div className="text-xs text-gray-400">Loading depth data...</div>
                  </div>
                );
              }
            })()}

              {/* Order Type & Product Type - Combined */}
              <div className="mb-3">
                <label className="text-gray-400 text-xs font-semibold mb-2 block">ORDER & PRODUCT TYPE</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setMarketType('LIMIT')}
                    className={`flex-1 py-1.5 rounded text-xs font-bold transition-colors ${
                      marketType === 'LIMIT'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    LMT
                  </button>
                  <button
                    onClick={() => setMarketType('MARKET')}
                    className={`flex-1 py-1.5 rounded text-xs font-bold transition-colors ${
                      marketType === 'MARKET'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    MKT
                  </button>
                  <button
                    onClick={() => setSegment('NRML')}
                    className={`flex-1 py-1.5 rounded text-xs font-bold transition-colors ${
                      segment === 'NRML'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    NRML
                  </button>
                  <button
                    onClick={() => setSegment('MIS')}
                    className={`flex-1 py-1.5 rounded text-xs font-bold transition-colors ${
                      segment === 'MIS'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    MIS
                  </button>
                </div>
              </div>

            {/* Quantity & Limit Price - Combined */}
            <div className="mb-3">
              <div className="grid grid-cols-2 gap-4">
                {/* Quantity */}
                <div>
                  <label className="text-gray-400 text-xs font-semibold mb-1 block">QTY</label>
                  <div className="flex gap-1 items-center justify-center">
                    <button
                      onClick={() => {
                        const currentQty = orderPopup.qty || lotSize;
                        const newQty = Math.max(0, currentQty - lotSize);
                        setOrderPopup({ ...orderPopup, qty: newQty });
                      }}
                      className="w-7 h-8 flex items-center justify-center rounded bg-red-500 hover:bg-red-600 text-white font-bold text-sm transition-colors flex-shrink-0"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      value={orderPopup.qty || lotSize}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || parseInt(value) >= 0) {
                          setOrderPopup({ ...orderPopup, qty: parseInt(value) || 0 });
                        }
                      }}
                      className="w-16 text-center text-sm font-bold text-gray-800 border border-gray-300 rounded h-8 focus:border-blue-500 focus:outline-none"
                      min="0"
                    />
                    <button
                      onClick={() => {
                        const currentQty = orderPopup.qty || lotSize;
                        const newQty = currentQty + lotSize;
                        setOrderPopup({ ...orderPopup, qty: newQty });
                      }}
                      className="w-7 h-8 flex items-center justify-center rounded bg-green-500 hover:bg-green-600 text-white font-bold text-sm transition-colors flex-shrink-0"
                    >
                      +
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-0.5 text-center">
                    Lot: {lotSize}
                  </p>
                </div>
                
                {/* Price (for Limit orders) */}
                {marketType === 'LIMIT' && (
                  <div>
                    <label className="text-gray-400 text-xs font-semibold mb-1 block">PRICE</label>
                    <input
                      type="number"
                      value={orderPopup.price || orderPopup.ltp}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        setOrderPopup({ ...orderPopup, price: value });
                      }}
                      step="0.05"
                      className="w-full text-center text-sm font-bold text-gray-800 border border-gray-300 rounded h-8 focus:border-blue-500 focus:outline-none max-w-[120px] mx-auto block"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-1">
              <button
                onClick={async () => {
                  try {
                    const qty = orderPopup.qty || lotSize;
                    const price = marketType === 'MARKET' ? 0 : (orderPopup.price || orderPopup.ltp);
                    
                    if (!qty || qty <= 0) {
                      setOrderMessage({ show: true, text: 'Please enter valid quantity', type: 'error' });
                      setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 3000);
                      return;
                    }
                    
                    if (marketType === 'LIMIT' && (!price || price <= 0)) {
                      setOrderMessage({ show: true, text: 'Please enter valid price', type: 'error' });
                      setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 3000);
                      return;
                    }
                    
                    const orderPayload = {
                      exchange: 'NFO',
                      tradingSymbol: orderPopup.tradingSymbol,
                      instrumentId: orderPopup.token,
                      transactionType: orderPopup.side,
                      quantity: qty,
                      price: price,
                      orderType: marketType,
                      validity: 'DAY',
                      product: segment,
                      orderComplexity: 'REGULAR',
                      deviceId: '123',
                      userId: userSession
                    };
                    
                    // If master user with groups, delegate to parent for group selection
                    if (isMasterUser && userGroups && userGroups.length > 0 && onGroupOrder) {
                      setOrderPopup({ show: false, strike: null, optionType: '', side: '', ltp: 0, token: null, tradingSymbol: '', qty: 0, price: 0 });
                      setPopupPos({ x: 0, y: 0 });
                      onGroupOrder(orderPayload);
                      return;
                    }
                    
                    console.log('📝 Placing order:', orderPayload);
                    
                    const response = await api.post('/orders/place', orderPayload, {
                      headers: { Authorization: `Bearer ${userSession}` }
                    });
                    
                    if (response.data.success) {
                      setOrderMessage({ 
                        show: true, 
                        text: `${orderPopup.side} order placed successfully! Order ID: ${response.data.data?.brokerOrderId || 'N/A'}`, 
                        type: 'success' 
                      });
                      setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 3000);
                      setOrderPopup({ show: false, strike: null, optionType: '', side: '', ltp: 0, token: null, tradingSymbol: '', qty: 0, price: 0 });
                      setPopupPos({ x: 0, y: 0 });
                    } else {
                      setOrderMessage({ 
                        show: true, 
                        text: 'Order failed: ' + (response.data.error || 'Unknown error'), 
                        type: 'error' 
                      });
                      setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 3000);
                    }
                  } catch (error) {
                    console.error('Order placement failed:', error);
                    setOrderMessage({ 
                      show: true, 
                      text: 'Failed to place order: ' + (error.response?.data?.error || error.message), 
                      type: 'error' 
                    });
                    setTimeout(() => setOrderMessage({ show: false, text: '', type: '' }), 3000);
                  }
                }}
                className={`flex-1 py-3 rounded-lg text-base font-bold text-white transition-colors shadow-lg ${
                  orderPopup.side === 'BUY'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {orderPopup.side === 'BUY' ? '📈 BUY' : '📉 SELL'}
              </button>
              <button
                onClick={() => { setOrderPopup({ show: false, strike: null, optionType: '', side: '', ltp: 0, token: null, tradingSymbol: '', qty: 0 }); setPopupPos({ x: 0, y: 0 }); }}
                className="flex-1 py-3 bg-gray-700 text-white rounded-lg text-base font-bold hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
            </div>
            </div>
          </div>,
          document.body
        );
      })()}
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(OptionChainFrame, (prevProps, nextProps) => {
  // Only re-render if these props change
  return (
    prevProps.symbol === nextProps.symbol &&
    prevProps.position === nextProps.position &&
    prevProps.isLocked === nextProps.isLocked &&
    prevProps.wsConnected === nextProps.wsConnected &&
    prevProps.livePrices === nextProps.livePrices
  );
});
